import { NodeResizer, useViewport } from 'reactflow';
import { useCallback, useRef, useState } from 'react';
import { useIsHovered } from '@/hooks/useIsHovered';
import { CustomGroupData } from '@/types/node';
import { color } from '@/colors';
import { useWorkflowStore } from '@/state/workflow.state';
import { NodeId } from '@/designer/designer';
import { LockClosedIcon } from '@/UI/Icons';
import { Flex, FlexCenHorVer } from '@/UI/styles';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { hexToRgbaWithAlpha } from '@/utils/color.utils';
import { useCustomGroupView, useEffectiveIsLocked, useFlowView } from '../Recipe/FlowContext';
import { useSelectionActions, consumeMultiSelectEvent } from '../Recipe/hooks/useSelectionActions';
import {
  DEFAULT_GROUP_COLOR,
  fontSizeToIconSizeTransformer,
  fontSizeToVariantTransformer,
  LABEL_SIZE,
} from '../Recipe/Views/NodeGrouping/groups.types';
import { EditableLabel } from '../Recipe/FlowComponents/Editor/Designer/LayerPanel/EditableLabel';
import { useGroupAttachementsHighlighting } from '../Recipe/hooks/useGroupNodeHighlighting';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import type { OnResizeEnd, OnResize, OnResizeStart } from '@reactflow/node-resizer';

const resolveColor = (color: string | undefined, opacity: number, defaultColor: string) => {
  return (color && hexToRgbaWithAlpha(color, opacity)) ?? hexToRgbaWithAlpha(defaultColor, opacity) ?? '';
};

const EDIT_OUTLINE = `1px solid ${color.White64_T}`;

export const CustomGroupNode = ({ id, data }: { id: NodeId; data: CustomGroupData }) => {
  const customGroupView = useCustomGroupView(id);
  const flowView = useFlowView();
  const { toggleNodeInSelection } = useSelectionActions();
  const highlightedNodeIds = useWorkflowStore((state) => state.highlightedNodeIds);
  const isHighlighted = highlightedNodeIds.includes(id);
  const { zoom } = useViewport();

  const { highlightPotentialGroupAttachements, clearAttachementHighlights } = useGroupAttachementsHighlighting();

  const handleResizeStart = useCallback<OnResizeStart>(
    (_, params) => {
      const { x, y, width, height } = params;
      customGroupView.handleResize({ x, y, width, height }, true);
    },
    [customGroupView],
  );

  const handleResize = useCallback<OnResize>(
    (_, params) => {
      const { x, y, width, height } = params;
      customGroupView.handleResize({ x, y, width, height }, true);
      highlightPotentialGroupAttachements([flowView.getNode(id)], []);
    },
    [customGroupView, highlightPotentialGroupAttachements, flowView, id],
  );

  const handleResizeEnd = useCallback<OnResizeEnd>(
    (_, params) => {
      const { x, y, width, height } = params;

      highlightPotentialGroupAttachements.cancel();
      customGroupView.handleResize({ x, y, width, height }, false);
      clearAttachementHighlights();
    },
    [customGroupView, highlightPotentialGroupAttachements, clearAttachementHighlights],
  );

  const handleToggleLock = useCallback(() => {
    customGroupView.toggleLock();
  }, [customGroupView]);

  const isSelected = flowView.getNode(id)?.selected;
  const { isHovered, ...hoverProps } = useIsHovered();
  const [isEditing, setIsEditing] = useState(false);
  const iconSize =
    fontSizeToIconSizeTransformer.get(data.labelFontSize as LABEL_SIZE) ??
    fontSizeToIconSizeTransformer.get(LABEL_SIZE.SMALL) ??
    16;
  const labelVariant =
    fontSizeToVariantTransformer.get(data.labelFontSize as LABEL_SIZE) ??
    fontSizeToVariantTransformer.get(LABEL_SIZE.SMALL) ??
    'body-lg-rg';
  const isLocked = useEffectiveIsLocked(id);
  const groupLabelRef = useRef<HTMLElement | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as globalThis.Node;
    // Clicks on the editable label should not start a group drag; ref matches the Typography root from EditableLabel.
    if (groupLabelRef.current?.contains(target)) {
      return;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && groupLabelRef.current?.contains(active)) {
      active.blur();
    }
  }, []);

  const handleChipClick = useCallback(
    (e: React.MouseEvent) => {
      if (!consumeMultiSelectEvent(e)) return;
      toggleNodeInSelection(id);
    },
    [id, toggleNodeInSelection],
  );

  // Figma: default 40%, hover 64%, selected/editing 80%
  const badgeBgOpacity = isSelected || isHighlighted || isEditing ? 0.8 : isHovered ? 0.64 : 0.4;
  // When locked + editing: border wraps label only. When unlocked + editing: border wraps whole badge.
  const borderOnBadge = isEditing && !isLocked;
  const borderOnLabel = isEditing && isLocked;
  const badgeHeight = iconSize + 8;

  const containerStrokeOpacity = isSelected || isHighlighted ? 0.8 : isHovered ? 0.4 : 0.16;

  return (
    // Wrapper div fills ReactFlow's node wrapper (which has explicit dimensions)
    // ReactFlow resizes this via direct DOM manipulation (fast, no React)
    <Flex
      {...hoverProps}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        // Don’t steal hits on the full node rect; badge / body / resizer opt in with pointer-events: auto when needed
        pointerEvents: 'none',
      }}
    >
      {/* Single container for both locked/unlocked — keeps height/position stable */}
      <FlexCenHorVer
        className="custom-group-drag-handle"
        onMouseDown={handleMouseDown}
        onClick={handleChipClick}
        sx={{
          position: 'absolute',
          top: '-8px',
          left: '0',
          transform: `translateY(calc(-100% / ${zoom})) scale(${1 / zoom})`,
          transformOrigin: 'top left',
          color: isSelected || isHighlighted ? color.White100 : color.White80_T,
          backgroundColor: resolveColor(data.color, badgeBgOpacity, DEFAULT_GROUP_COLOR),
          ...(borderOnBadge && { outline: EDIT_OUTLINE }),
          px: 0.5,
          py: 0.25,
          height: badgeHeight,
          alignItems: 'center',
          borderRadius: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: `calc(100% * ${zoom})`,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      >
        {isLocked && (
          <AppIconButton
            onClick={handleToggleLock}
            width={iconSize}
            height={iconSize}
            mode="on-dark"
            sx={{
              mx: 0.25,
              // Stay above the label flex so hover/press reliably hit the button (not the stretched label row).
              position: 'relative',
              zIndex: 1,
            }}
          >
            <LockClosedIcon width={iconSize} height={iconSize} />
          </AppIconButton>
        )}
        <FlexCenHorVer
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            alignSelf: 'stretch',
            ...(borderOnLabel && { outline: EDIT_OUTLINE, borderRadius: 1 }),
          }}
        >
          <EditableLabel
            ref={groupLabelRef}
            className={isEditing ? 'nodrag' : undefined}
            variant={labelVariant}
            value={data.name}
            onSubmit={(newName) => customGroupView.updateGroupName(newName)}
            showHoverBackground={false}
            noBorder
            onEditingChange={setIsEditing}
            sx={{
              color: isSelected || isHighlighted ? color.White100 : color.White80_T,
              minWidth: 0,
              flex: 1,
              padding: '2px 0',
            }}
          />
        </FlexCenHorVer>
      </FlexCenHorVer>
      <DynamicNode2
        id={id}
        data={data}
        hideHandles
        hideTitle
        className="custom-group-main custom-group-drag-handle"
        sx={{
          width: '100%',
          height: '100%',
          border: `2px solid ${resolveColor(data.color, containerStrokeOpacity, DEFAULT_GROUP_COLOR)}`,
          backgroundColor: resolveColor(data.color, isSelected || isHighlighted ? 0.24 : 0.16, DEFAULT_GROUP_COLOR),
          transition: 'all 0.2s ease-in-out',
          p: 0,
          pointerEvents: isSelected ? 'auto' : 'none',
        }}
      />
      <Flex sx={{ pointerEvents: isSelected ? 'auto' : 'none' }}>
        <NodeResizer
          nodeId={id}
          isVisible={true}
          lineStyle={{ borderColor: '#00000000', borderWidth: '10px' }}
          keepAspectRatio={false}
          handleStyle={{
            backgroundColor: '#00000000',
            borderColor: '#00000000',
            borderWidth: '10px',
          }}
          onResizeStart={handleResizeStart}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
        />
      </Flex>
    </Flex>
  );
};
